import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Globe, Trash2, RefreshCw, Search, ChevronRight, TrendingUp, Users, Star, AlertCircle } from 'lucide-react';
import { prospectsApi, extractDomain } from '../api/prospects';
import type { Prospect, ProspectStatus } from '../types';
import { useLang } from '../context/LangContext';

const STATUS_CFG: Record<ProspectStatus, { ro: string; en: string; color: string; dot: string }> = {
  new:       { ro: 'Nou',        en: 'New',       color: 'bg-blue-500/20 text-blue-400',    dot: 'bg-blue-400' },
  contacted: { ro: 'Contactat',  en: 'Contacted', color: 'bg-yellow-500/20 text-yellow-400', dot: 'bg-yellow-400' },
  qualified: { ro: 'Calificat',  en: 'Qualified', color: 'bg-purple-500/20 text-purple-400', dot: 'bg-purple-400' },
  proposal:  { ro: 'Propunere',  en: 'Proposal',  color: 'bg-orange-500/20 text-orange-400', dot: 'bg-orange-400' },
  lost:      { ro: 'Pierdut',    en: 'Lost',      color: 'bg-red-500/20 text-red-400',       dot: 'bg-red-400' },
  converted: { ro: 'Convertit',  en: 'Converted', color: 'bg-green-500/20 text-green-400',   dot: 'bg-green-400' },
};

const ScoreCircle = ({ score, size = 48 }: { score: number; size?: number }) => {
  const c = score >= 90 ? '#4ade80' : score >= 50 ? '#facc15' : '#f87171';
  const r = (size - 6) / 2;
  const circ = 2 * Math.PI * r;
  const dash = (score / 100) * circ;
  return (
    <svg width={size} height={size} className="flex-shrink-0">
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth={5} />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={c} strokeWidth={5}
        strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
        transform={`rotate(-90 ${size/2} ${size/2})`} />
      <text x={size/2} y={size/2 + 4} textAnchor="middle" fill={c} fontSize={size < 40 ? 9 : 11} fontWeight="700">{score}</text>
    </svg>
  );
};

function AddProspectModal({ onClose, onAdd }: { onClose: () => void; onAdd: (url: string, name: string, notes: string, priority: 'low'|'medium'|'high') => void }) {
  const [url, setUrl] = useState('');
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [priority, setPriority] = useState<'low'|'medium'|'high'>('medium');

  const handleAdd = () => {
    if (!url.trim()) return;
    const fullUrl = url.startsWith('http') ? url : 'https://' + url;
    if (!name) setName(extractDomain(fullUrl));
    onAdd(fullUrl, name || extractDomain(fullUrl), notes, priority);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-[#0a0c12] border border-white/15 rounded-2xl w-full max-w-md p-6 shadow-2xl mx-4">
        <h2 className="text-lg font-bold text-white mb-1">Prospect nou</h2>
        <p className="text-xs text-white/40 mb-5">Adaugă URL-ul site-ului și rulăm analiza automată</p>

        <div className="space-y-4">
          <div>
            <label className="block text-xs text-white/40 mb-1.5 uppercase tracking-wide font-semibold">URL Website *</label>
            <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-lg px-3 py-2.5">
              <Globe size={15} className="text-white/30 flex-shrink-0" />
              <input value={url} onChange={e => setUrl(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleAdd()}
                placeholder="https://exemplu.ro" className="flex-1 bg-transparent text-sm text-white placeholder-white/20 outline-none" autoFocus />
            </div>
          </div>
          <div>
            <label className="block text-xs text-white/40 mb-1.5 uppercase tracking-wide font-semibold">Nume prospect</label>
            <input value={name} onChange={e => setName(e.target.value)}
              placeholder="Ex: TechSoft SRL (opțional)"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder-white/20 outline-none focus:border-[#295CA8] transition-colors" />
          </div>
          <div>
            <label className="block text-xs text-white/40 mb-1.5 uppercase tracking-wide font-semibold">Prioritate</label>
            <div className="flex gap-2">
              {(['low','medium','high'] as const).map(p => (
                <button key={p} onClick={() => setPriority(p)}
                  className={`flex-1 py-2 rounded-lg text-xs font-semibold border transition-all ${priority === p
                    ? p === 'high' ? 'bg-red-500/20 border-red-500/40 text-red-400'
                      : p === 'medium' ? 'bg-yellow-500/20 border-yellow-500/40 text-yellow-400'
                      : 'bg-blue-500/20 border-blue-500/40 text-blue-400'
                    : 'bg-white/5 border-white/10 text-white/30 hover:text-white'}`}>
                  {p === 'low' ? 'Scăzută' : p === 'medium' ? 'Medie' : 'Înaltă'}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs text-white/40 mb-1.5 uppercase tracking-wide font-semibold">Note inițiale</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
              placeholder="Observații despre prospect..."
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-white/20 outline-none focus:border-[#295CA8] transition-colors resize-none" />
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-lg text-sm text-white/50 hover:text-white bg-white/5 hover:bg-white/10 transition-all">Anulează</button>
          <button onClick={handleAdd} disabled={!url.trim()}
            className="flex-1 py-2.5 rounded-lg text-sm font-semibold bg-[#295CA8] hover:bg-[#3D72B8] text-white transition-all disabled:opacity-40 flex items-center justify-center gap-2">
            <Plus size={15} /> Adaugă & Analizează
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ProspectsPage() {
  const { lang } = useLang();
  const navigate = useNavigate();
  const [prospects, setProspects] = useState<Prospect[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [showModal, setShowModal] = useState(false);
  const [analyzing, setAnalyzing] = useState<Set<string>>(new Set());

  const load = async () => { setLoading(true); setProspects(await prospectsApi.list()); setLoading(false); };
  useEffect(() => { load(); }, []);

  const handleAdd = async (url: string, name: string, notes: string, priority: 'low'|'medium'|'high') => {
    const p = await prospectsApi.create({ url, name, notes, priority, status: 'new', contactName: '', contactEmail: '', contactPhone: '' });
    setProspects(prev => [p, ...prev]);
    // Auto-start analysis
    runAnalysis(p.id, p.url);
    navigate(`/prospects/${p.id}`);
  };

  const runAnalysis = async (id: string, url: string) => {
    setAnalyzing(prev => new Set([...prev, id]));
    setProspects(prev => prev.map(p => p.id === id ? { ...p, analysisStatus: 'analyzing' } : p));
    const res = await prospectsApi.analyze(id, url);
    setProspects(prev => prev.map(p => p.id === id ? { ...p, analysisStatus: res.ok ? 'done' : 'error', analysis: res.analysis } : p));
    setAnalyzing(prev => { const n = new Set(prev); n.delete(id); return n; });
  };

  const handleDelete = async (id: string, e: React.MouseEvent) => {
    e.preventDefault(); e.stopPropagation();
    if (!confirm('Ștergi prospectul?')) return;
    await prospectsApi.delete(id);
    setProspects(prev => prev.filter(p => p.id !== id));
  };

  const filtered = prospects.filter(p => {
    const q = search.toLowerCase();
    const matchSearch = !q || p.name.toLowerCase().includes(q) || p.domain.toLowerCase().includes(q) || p.url.toLowerCase().includes(q);
    const matchStatus = filterStatus === 'all' || p.status === filterStatus;
    return matchSearch && matchStatus;
  });

  // Stats
  const total = prospects.length;
  const qualified = prospects.filter(p => p.status === 'qualified' || p.status === 'proposal').length;
  const converted = prospects.filter(p => p.status === 'converted').length;
  const avgScore = prospects.filter(p => p.analysis?.pagespeed).length
    ? Math.round(prospects.filter(p => p.analysis?.pagespeed).reduce((s, p) => s + (p.analysis!.pagespeed!.mobile.score), 0) / prospects.filter(p => p.analysis?.pagespeed).length)
    : 0;

  return (
    <div className="space-y-6">
      {showModal && <AddProspectModal onClose={() => setShowModal(false)} onAdd={handleAdd} />}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Prospecți</h1>
          <p className="text-sm text-white/40 mt-1">Analiză automată site-uri potențiali clienți</p>
        </div>
        <button onClick={() => setShowModal(true)}
          className="inline-flex items-center gap-2 bg-[#295CA8] hover:bg-[#3D72B8] text-white px-4 py-2.5 rounded-lg text-sm font-semibold transition-all">
          <Plus size={16} /> Prospect nou
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { icon: Globe, label: 'Total prospecți', value: total, color: 'text-white' },
          { icon: Star, label: 'Calificați', value: qualified, color: 'text-purple-400' },
          { icon: TrendingUp, label: 'Convertiți', value: converted, color: 'text-green-400' },
          { icon: AlertCircle, label: 'Scor mediu mobile', value: avgScore ? `${avgScore}/100` : 'N/A', color: avgScore >= 70 ? 'text-green-400' : avgScore >= 50 ? 'text-yellow-400' : 'text-red-400' },
        ].map(({ icon: Icon, label, value, color }) => (
          <div key={label} className="bg-[#07090f] border border-white/10 rounded-xl px-4 py-3">
            <div className="flex items-center gap-2 text-white/30 text-xs mb-1"><Icon size={13} />{label}</div>
            <div className={`text-xl font-bold ${color}`}>{value}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-48">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Caută prospect, domeniu..."
            className="w-full pl-9 pr-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#295CA8] transition-colors" />
        </div>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          className="bg-[#0a0c12] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#295CA8] transition-colors">
          <option value="all">Toate statusurile</option>
          {(Object.keys(STATUS_CFG) as ProspectStatus[]).map(s => (
            <option key={s} value={s}>{STATUS_CFG[s].ro}</option>
          ))}
        </select>
      </div>

      {/* List */}
      {loading ? (
        <div className="py-20 text-center text-white/30 text-sm"><RefreshCw size={24} className="animate-spin mx-auto mb-3 opacity-40" />Se încarcă...</div>
      ) : filtered.length === 0 ? (
        <div className="py-20 text-center border border-dashed border-white/10 rounded-2xl">
          <Users size={36} className="mx-auto mb-3 text-white/20" />
          <p className="text-white/30 text-sm">Niciun prospect găsit.</p>
          <button onClick={() => setShowModal(true)} className="mt-4 text-[#4ab8ff] text-sm hover:underline">Adaugă primul prospect →</button>
        </div>
      ) : (
        <div className="space-y-2.5">
          {filtered.map(p => {
            const isAnalyzing = analyzing.has(p.id) || p.analysisStatus === 'analyzing';
            const mob = p.analysis?.pagespeed?.mobile;
            const desk = p.analysis?.pagespeed?.desktop;
            const sc = p.analysis;

            return (
              <Link key={p.id} to={`/prospects/${p.id}`}
                className="flex items-center gap-4 bg-[#07090f] border border-white/10 hover:border-[#295CA8]/40 rounded-xl px-5 py-4 transition-all group">
                {/* Score */}
                <div className="flex-shrink-0">
                  {isAnalyzing ? (
                    <div className="w-12 h-12 flex items-center justify-center">
                      <RefreshCw size={20} className="animate-spin text-[#4ab8ff]" />
                    </div>
                  ) : mob ? <ScoreCircle score={mob.score} size={48} /> : (
                    <div className="w-12 h-12 rounded-full bg-white/5 border border-white/10 flex items-center justify-center">
                      <Globe size={18} className="text-white/20" />
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2.5 flex-wrap">
                    <span className="font-semibold text-white truncate">{p.name || p.domain}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_CFG[p.status].color}`}>
                      {lang === 'ro' ? STATUS_CFG[p.status].ro : STATUS_CFG[p.status].en}
                    </span>
                    {p.priority === 'high' && <span className="px-2 py-0.5 rounded-full text-xs bg-red-500/20 text-red-400">🔥 Prioritar</span>}
                    {sc?.platform && <span className="px-2 py-0.5 rounded-full text-xs bg-white/10 text-white/50">{sc.platform}</span>}
                  </div>
                  <div className="flex items-center gap-3 mt-1 flex-wrap">
                    <span className="text-xs text-white/30">{p.domain}</span>
                    {isAnalyzing && <span className="text-xs text-[#4ab8ff] flex items-center gap-1"><RefreshCw size={10} className="animate-spin" />Se analizează...</span>}
                    {sc?.seo && <span className="text-xs text-white/30">SEO: <span className={sc.seo.score >= 70 ? 'text-green-400' : sc.seo.score >= 50 ? 'text-yellow-400' : 'text-red-400'}>{sc.seo.score}/100</span></span>}
                    {desk && <span className="text-xs text-white/30">Desktop: <span className={desk.score >= 70 ? 'text-green-400' : desk.score >= 50 ? 'text-yellow-400' : 'text-red-400'}>{desk.score}</span></span>}
                    {sc?.whois?.ownerName && <span className="text-xs text-white/30 truncate max-w-32">🏢 {sc.whois.ownerName}</span>}
                  </div>
                </div>

                {/* Issues count */}
                {sc?.seo?.issues && sc.seo.issues.length > 0 && (
                  <div className="flex-shrink-0 text-center hidden md:block">
                    <div className="text-sm font-bold text-red-400">{sc.seo.issues.length}</div>
                    <div className="text-xs text-white/30">probleme</div>
                  </div>
                )}

                {/* Revenue */}
                {sc?.company?.revenue && sc.company.revenue.length > 0 && (
                  <div className="flex-shrink-0 text-right hidden lg:block">
                    <div className="text-sm font-bold text-white">
                      {(sc.company.revenue[sc.company.revenue.length-1].cifraAfaceri / 1000).toFixed(0)}K RON
                    </div>
                    <div className="text-xs text-white/30">CA {sc.company.revenue[sc.company.revenue.length-1].year}</div>
                  </div>
                )}

                <div className="flex items-center gap-2 flex-shrink-0">
                  {!isAnalyzing && (
                    <button onClick={e => { e.preventDefault(); e.stopPropagation(); runAnalysis(p.id, p.url); }}
                      title="Re-analizează" className="p-2 rounded-lg text-white/20 hover:text-[#4ab8ff] hover:bg-[#295CA8]/10 transition-all">
                      <RefreshCw size={14} />
                    </button>
                  )}
                  <button onClick={e => handleDelete(p.id, e)}
                    title="Șterge" className="p-2 rounded-lg text-white/20 hover:text-red-400 hover:bg-red-500/10 transition-all">
                    <Trash2 size={14} />
                  </button>
                  <ChevronRight size={16} className="text-white/20 group-hover:text-[#4ab8ff] transition-colors" />
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
